import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { useQuery } from 'react-query'
import { getLoanCandidates } from 'services/fund'
import { useScoreFrom } from 'components/memberScore'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import useGuarantorSuggestion from 'pages/loans/manager/addNew/hooks/useGuarantorSuggestion'
import { faPlain, longJalali, toman } from 'utilities/helper/format'
import WizardChrome, { IReceiptLine } from './components/WizardChrome'
import StickyAction from './components/StickyAction'
import StepBorrower from './steps/StepBorrower'
import StepAmount from './steps/StepAmount'
import StepTerms, { TermsSummary } from './steps/StepTerms'
import GuarantorSection from './components/GuarantorSection'
import useGrantDraft from './useGrantDraft'
import StepReview from './steps/StepReview'
import useGrantSubmit, { IGrantDraft } from './useGrantSubmit'
import useWizardHistory from './useWizardHistory'
import useDraftSchedule, {
    MIN_AMOUNT,
    resolveSchedule,
    scheduleBasis,
} from './useDraftSchedule'
import {
    firstDateOutOfOrder,
    patternLabel,
    SchedulePattern,
    scheduleSum,
} from './schedulePatterns'

/**
 * Three steps: who, on what terms, and sign-off.
 *
 * It was five — گیرنده، مبلغ، ضامن، شرایط، بازبینی — and a fund manager who
 * grants loans every week reported the new flow as slower than the one long
 * form it replaced (pwa#128). The amount, the optional guarantor and the
 * repayment terms are one decision about one loan, and splitting them across
 * three screens mostly bought three «ادامه» taps. They share a page now; the
 * borrower (a list you scroll) and the review (where the money moves) keep
 * their own.
 *
 * `?step=4` / `?step=5` from an old link or a restored tab clamp to 3, and
 * the guard below walks them back to 2 when the terms are not filled in.
 */
const TOTAL_STEPS = 3
/** sent with every wizard event, so the 5-step funnel stays separable */
const WIZARD_VERSION = 2

const STEP_TITLES: Record<number, { title: string; subtitle?: string }> = {
    1: {
        title: 'به چه کسی وام می‌دهید؟',
        subtitle:
            'اعضا بر اساس امتیاز اعتباری مرتب شده‌اند — یعنی چقدر پول، چند روز، در صندوق گذاشته‌اند. امتیاز فقط راهنماست؛ تصمیم با شماست.',
    },
    2: { title: 'مبلغ و شرایط وام' },
    3: { title: 'بازبینی و ثبت' },
}

const BLOCKED_MESSAGES = {
    amount_below_min: 'مبلغ وام را وارد کنید',
    installments_missing: 'تعداد اقساط را وارد کنید',
    schedule_pending: 'جدول اقساط هنوز ساخته نشده؛ چند لحظه صبر کنید.',
    schedule_sum_mismatch:
        'جمع اقساط با مبلغ بازپرداخت یکی نیست؛ جدول اقساط را اصلاح کنید.',
    schedule_dates: 'سررسید هر قسط باید بعد از قسط قبلی باشد.',
} as const

/**
 * اعطای وام, as a wizard.
 *
 * The screen this replaces was one long form whose first field was «مبلغ
 * وام». That is the second question. The first — «به چه کسی؟» — got a bare
 * name picker and no information at all, which meant the manager of a
 * 500-member fund answered the most consequential question on the screen
 * from memory.
 *
 * Each step asks one thing and then collapses into a line of the receipt at
 * the top, so nothing is hidden and every answer stays one tap away. The
 * step lives in the URL (`?step=`) rather than in state alone so the phone's
 * back button walks the wizard instead of abandoning it — losing four
 * answered questions to a stray back-swipe is the classic way a wizard gets
 * worse than the form it replaced.
 */
export default function GrantLoanWizard() {
    const params = useParams()
    const { state } = useLocation()
    const [searchParams] = useSearchParams()

    const moneypoolId = params?.id
    const step = Math.min(
        Math.max(Number(searchParams.get('step') || 1), 1),
        TOTAL_STEPS
    )

    // survives the trip to /manage-bank-cards/add and back
    const {
        borrower,
        setBorrower,
        amount,
        setAmount,
        terms,
        setTerms,
        guarantors,
        setGuarantors,
        note,
        setNote,
        nudgeId,
        setNudgeId,
        clear: clearDraft,
    } = useGrantDraft(moneypoolId)
    // the card is deliberately NOT persisted: it is owned by useGetCards,
    // which re-reads the account list on the way back — exactly when the
    // manager has just added the account they were missing
    const [card, setCard] = useState<IBankAccountCards | undefined>()

    const suggestion = useGuarantorSuggestion(
        moneypoolId,
        borrower?.poolship_id
    )

    const draft: IGrantDraft = {
        moneypoolId,
        fundName: state?.name,
        borrower,
        amount,
        terms,
        guarantors,
        note,
        card,
        nudgeId,
    }
    const { submit, isSubmitting } = useGrantSubmit(draft, clearDraft)

    // The schedule the loan will carry, previewed while the terms are typed —
    // and the same one `useGrantSubmit` sends, because both resolve it from
    // the same draft and the same `terms.pattern` / `terms.custom`.
    const scheduleQuery = useDraftSchedule(moneypoolId, amount, terms)
    const schedule = scheduleQuery.draft
        ? resolveSchedule(scheduleQuery.draft, amount, terms)
        : undefined

    const choosePattern = (pattern: SchedulePattern) => {
        setTerms((current) => ({ ...current, pattern, custom: null }))
        posthog.capture('loan_grant_schedule_pattern_selected', {
            fund_id: moneypoolId,
            wizard_version: WIZARD_VERSION,
            pattern,
            installments: Number(terms.installments || 0),
            amount: Number(amount || 0),
        })
    }

    const keepRows = (rows: IInstallment[]) =>
        setTerms((current) => ({
            ...current,
            custom: {
                basis: scheduleBasis(amount, current),
                installments: rows,
            },
        }))

    const editRows = (rows: IInstallment[], index: number) => {
        keepRows(rows)
        posthog.capture('loan_grant_installment_edited', {
            fund_id: moneypoolId,
            wizard_version: WIZARD_VERSION,
            index,
            installments: rows.length,
            // what the manager started from before touching a row
            pattern: terms.pattern ?? 'equal',
        })
    }

    const fixRemainder = (rows: IInstallment[]) => {
        keepRows(rows)
        posthog.capture('loan_grant_schedule_remainder_fixed', {
            fund_id: moneypoolId,
            wizard_version: WIZARD_VERSION,
        })
    }

    const resetSchedule = () => {
        setTerms((current) => ({ ...current, custom: null }))
        posthog.capture('loan_grant_schedule_reset', {
            fund_id: moneypoolId,
            wizard_version: WIZARD_VERSION,
            pattern: terms.pattern ?? 'equal',
        })
    }

    // Latch the nudge id out of the URL on arrival. `setSearchParams` (in
    // `applyBorrower` and in the step navigation) REPLACES the query string,
    // so this is the only window in which `?nudge=` is still readable — and
    // before this existed every completed grant reported `nudge_id: null`.
    useEffect(() => {
        setNudgeId(searchParams.get('nudge'))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams])

    useEffect(() => {
        posthog.capture('loan_grant_step_viewed', {
            fund_id: moneypoolId,
            step,
            wizard_version: WIZARD_VERSION,
        })
    }, [step, moneypoolId])

    // The wizard is one mounted component whose step lives in `?step=`, so the
    // effect above runs on every step change. «شروع» must not: it is the
    // denominator of the whole funnel, and counting it five times per grant
    // would put the completion rate at a fifth of the truth.
    const hasStarted = useRef(false)
    useEffect(() => {
        if (hasStarted.current) return
        hasStarted.current = true
        posthog.capture('loan_grant_started', {
            fund_id: moneypoolId,
            flow: 'wizard',
            wizard_version: WIZARD_VERSION,
            // `src` is what the Bale nudge's deep link sets, so a grant that
            // started from a notification is separable from one that started
            // in the app — the whole point of the feature is the difference.
            entry_source: searchParams.get('src') ?? state?.source ?? 'unknown',
            nudge_id: searchParams.get('nudge') || nudgeId || null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moneypoolId])

    /**
     * The arrow, the receipt's «ویرایش» and the phone's back button walk ONE
     * linear stack of steps — see useWizardHistory.
     *
     * History worth keeping: the arrow was first `navigate(-1)`, which walked
     * FORWARD after a receipt jump (the stack was a log of visits); then it
     * pushed «step − 1», which fixed the arrow and made the phone's back
     * button bounce 2 → 3 → 2. Both are the same mistake — letting the stack
     * stop being «1, 2, …, current».
     *
     * `{ step }` alone on purpose: the one-shot `?borrower=` / `?nudge=` link
     * parameters are applied once and must not ride along to later steps.
     */
    const stepNav = useWizardHistory({
        step,
        paramsFor: (next) => ({ step: String(next) }),
    })
    const goTo = (next: number) => stepNav.forward(next)
    const back = stepNav.back

    const selectBorrower = (
        candidate: ILoanCandidate,
        scope: 'requests' | 'all'
    ) => {
        applyBorrower(candidate, 'picker', {
            // a request answered from «همهٔ اعضا» is linked all the same, so
            // this is how often a request is met without its queue being open
            list_scope: scope,
            // whether the queue is worked in the order the members' own
            // deadlines imply, or in score order regardless — the whole
            // reason فوریت is now on the row
            demand_priority: candidate.demand?.priority || null,
            demand_has_note: !!candidate.demand?.description?.trim(),
        })
    }

    /**
     * «اعطای وام به این عضو» on کارنامهٔ عضو already answered step 1.
     *
     * The manager picked the person, then read a page about them, then asked
     * to lend to them — putting the name picker in front of them again asks
     * the one question they have most decisively already answered, and on a
     * 500-member fund it also asks them to find that name a second time.
     *
     * Applied once, on mount: after that the manager owns the wizard, and
     * re-running this would overwrite a borrower they deliberately changed on
     * a walk back to step 1. `replace` rather than push, so the back arrow
     * returns to the کارنامه they came from instead of to a step they were
     * never shown.
     */
    /**
     * Answer step 1 on the manager's behalf and move them to step 2.
     *
     * Shared by the two ways that can happen — «اعطای وام به این عضو» on
     * کارنامهٔ عضو (router state) and a Bale nudge button (a URL) — so the
     * two cannot drift on what prefilling means or which event is captured.
     *
     * `replace`, not push: the back arrow should return to wherever they came
     * from, not to a step they were never shown.
     */
    const applyBorrower = (
        candidate: ILoanCandidate,
        source: string,
        extra: Record<string, unknown> = {}
    ) => {
        setBorrower(candidate)
        // A member who filed a request already answered the next two
        // questions; prefilling is not a decision, it is not retyping.
        //
        // Written UNCONDITIONALLY, not only when there is a demand: the draft
        // outlives the screen in sessionStorage, so a wizard reopened on a
        // DIFFERENT borrower would otherwise keep the previous one's amount
        // and instalment count and present them as this member's request.
        setAmount(candidate.demand ? String(candidate.demand.amount) : '')
        setTerms((current) => ({
            ...current,
            installments: candidate.demand
                ? String(candidate.demand.num_repayments ?? '')
                : '',
            // hand edits describe the previous borrower's loan
            custom: null,
        }))
        posthog.capture('loan_grant_step_completed', {
            fund_id: moneypoolId,
            step: 1,
            flow: 'wizard',
            wizard_version: WIZARD_VERSION,
            from_demand: !!candidate.demand,
            preselected: source !== 'picker',
            preselect_source: source,
            // joins this click back to the exact LoanGrantNudge row that
            // offered the member, so "sent" and "opened" are one funnel
            // rather than two unrelated counters
            nudge_id: searchParams.get('nudge') || nudgeId || null,
            ...extra,
        })
        // From the picker this PUSHES, so the back arrow returns to the list
        // the manager was just reading. From a preselect it REPLACES, so back
        // leaves for wherever they came from rather than showing them a step
        // they were never given.
        if (source === 'picker') stepNav.forward(2)
        else stepNav.replace(2)
    }

    const preselected = useRef(false)
    useEffect(() => {
        if (preselected.current) return
        preselected.current = true
        const handed = state?.borrower as ILoanCandidate | undefined
        if (!handed) return
        // `source` says who handed it over — کارنامهٔ عضو, or the loan
        // request's own «بررسی و اعطای وام» (`cartable`), which is the case
        // the owner asked for: the manager is standing on one request, so
        // «به چه کسی؟» is already answered.
        applyBorrower(handed, state?.source || 'state')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * The same shortcut, but reachable from outside the app.
     *
     * The Bale «الان می‌توانی وام بدهی» message carries one glass button per
     * candidate, and a bot button can only hand over a URL — there is no
     * router `state` to put an ILoanCandidate in. So the link names the
     * borrower by poolship id and the row is fetched here.
     *
     * `requests_only=0`: the notification is sent about somebody with an open
     * request, but by the time the manager taps it that request may already
     * have been granted, which drops them out of the requests-only list. The
     * full list still has them, so the wizard opens on the right person and
     * simply asks the amount instead of dead-ending on «پیدا نشد».
     */
    const wantedPoolshipId = Number(searchParams.get('borrower')) || undefined
    // Compare IDENTITIES, not emptiness. `useGrantDraft` restores the draft
    // from sessionStorage on mount, so after the manager taps the first
    // candidate button in a Bale message the wizard already HAS a borrower.
    // With `!borrower` the query was then disabled for every later button in
    // that same message, the lookup never ran, and the wizard silently opened
    // on the FIRST button's member — reported from the very first real
    // message: tapping «حاج آقا لطیفی» or «امیرحسین فرامرزی» both landed on
    // «سلمه تیموری».
    const needsLookup =
        !!wantedPoolshipId && borrower?.poolship_id !== wantedPoolshipId
    // only a name lookup, but on the list's key so it is the same fetch
    const [scoreFrom] = useScoreFrom(moneypoolId)
    const { data: lookup, isError: lookupFailed } = useQuery(
        ['loan-candidates', moneypoolId, false, false, scoreFrom],
        getLoanCandidates,
        { enabled: !!moneypoolId && needsLookup, staleTime: 60_000 }
    )

    // Which link has been honoured, by borrower id rather than a bare boolean
    // — a second nudge button opened in the same session names a different
    // member and must still be applied.
    const linkAppliedFor = useRef<number | undefined>(undefined)
    useEffect(() => {
        if (!wantedPoolshipId) return
        if (linkAppliedFor.current === wantedPoolshipId) return
        if (borrower?.poolship_id === wantedPoolshipId) {
            // already the right person (restored draft, or we just applied it)
            linkAppliedFor.current = wantedPoolshipId
            return
        }
        if (lookupFailed) {
            linkAppliedFor.current = wantedPoolshipId
            toast.error('گیرندهٔ این لینک پیدا نشد؛ از فهرست انتخاب کنید.')
            return
        }
        if (!lookup) return
        const match = lookup.results?.find(
            (row) => row.poolship_id === wantedPoolshipId
        )
        linkAppliedFor.current = wantedPoolshipId
        if (!match) {
            toast.info('این درخواست دیگر باز نیست؛ از فهرست انتخاب کنید.')
            stepNav.replace(1)
            return
        }
        applyBorrower(match, searchParams.get('src') || 'link')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lookup, lookupFailed, wantedPoolshipId, borrower])

    const receipt = useMemo<IReceiptLine[]>(() => {
        const lines: IReceiptLine[] = []
        if (step > 1 && borrower) {
            lines.push({
                label: 'گیرنده',
                value: borrower.member_name,
                step: 1,
            })
            // The grant closes this request as accepted. Kept on every later
            // step, not only the review, so it is never news at the end.
            // It jumps to step 1 like «گیرنده»: another borrower is the only
            // way to grant without it.
            if (borrower.demand) {
                const filed = longJalali(borrower.demand.requested_date)
                lines.push({
                    label: 'درخواست وام',
                    value: filed ? `${filed}، وصل می‌شود` : 'وصل می‌شود',
                    step: 1,
                })
            }
        }
        if (step > 2 && Number(amount) > 0) {
            lines.push({ label: 'مبلغ', value: toman(Number(amount)), step: 2 })
        }
        if (step > 2 && Number(terms.installments) > 0) {
            const shape = schedule?.edited
                ? 'دلخواه'
                : (terms.pattern ?? 'equal') !== 'equal'
                ? patternLabel(terms.pattern)
                : ''
            lines.push({
                label: 'اقساط',
                value: `${faPlain.format(Number(terms.installments))} قسط${
                    shape ? ` — ${shape}` : ''
                }`,
                step: 2,
            })
        }
        if (step > 2) {
            lines.push({
                label: 'ضامن',
                value: guarantors.length
                    ? guarantors.map((item) => item.name).join('، ')
                    : 'ندارد',
                step: 2,
            })
        }
        return lines
    }, [step, borrower, amount, guarantors, terms, schedule?.edited])

    const hasTerms =
        Number(amount) >= MIN_AMOUNT &&
        Number.isInteger(Number(terms.installments)) &&
        Number(terms.installments) >= 1

    /**
     * Why «ادامه» on step 2 is refused, or undefined when it is not.
     *
     * The schedule rules are the backend's, checked here because the backend
     * enforces them SILENTLY — a schedule whose rows do not add up is simply
     * replaced by the equal split and the loan is created anyway. The manager
     * would find out from the borrower's first instalment.
     */
    const blockedReason = (): keyof typeof BLOCKED_MESSAGES | undefined => {
        if (step !== 2) return undefined
        if (!(Number(amount) >= MIN_AMOUNT)) return 'amount_below_min'
        if (!hasTerms) return 'installments_missing'
        if (!scheduleQuery.draft || !schedule) {
            // An equal split needs no preview: the submit builds it from the
            // same draft call, exactly as it always has. Anything else must
            // be seen before it is granted.
            const wantsCustom =
                (terms.pattern ?? 'equal') !== 'equal' ||
                terms.custom?.basis === scheduleBasis(amount, terms)
            return wantsCustom ? 'schedule_pending' : undefined
        }
        if (
            scheduleSum(schedule.installments) !==
            scheduleSum(scheduleQuery.draft)
        ) {
            return 'schedule_sum_mismatch'
        }
        if (firstDateOutOfOrder(schedule.installments) !== undefined) {
            return 'schedule_dates'
        }
        return undefined
    }

    const onContinue = () => {
        const reason = blockedReason()
        if (reason) {
            // the silent half of the funnel: the manager pressed «ادامه» and
            // the wizard refused. Only a toast said so, so a step people could
            // not get past looked exactly like a step they chose to abandon.
            posthog.capture('loan_grant_step_blocked', {
                fund_id: moneypoolId,
                step,
                wizard_version: WIZARD_VERSION,
                reason,
            })
            toast.error(BLOCKED_MESSAGES[reason])
            return
        }
        posthog.capture('loan_grant_step_completed', {
            fund_id: moneypoolId,
            step,
            flow: 'wizard',
            wizard_version: WIZARD_VERSION,
            from_demand: !!borrower?.demand,
            guarantors: guarantors.length,
            fee_type: Number(terms.fee || 0) > 0 ? terms.feeType : 'none',
            schedule_pattern: terms.pattern ?? 'equal',
            schedule_edited: !!schedule?.edited,
        })
        goTo(step + 1)
    }

    // a deep link to a later step with nothing filled in is a dead end --
    // UNLESS a `?borrower=` lookup is still in flight, which is exactly what
    // the Bale nudge's link opens with. This guard runs on the first render,
    // before that fetch can resolve, and `setSearchParams` replaces the whole
    // query string: left unqualified it bounced to step 1 AND dropped the
    // `borrower` param, so the lookup could never complete and the button
    // silently landed the manager back on the name picker.
    //
    // Likewise the review with no terms: `?step=5` from before the wizard
    // was shortened clamps to 3, and a review of a loan with no amount can
    // only fail at the last button.
    useEffect(() => {
        if (step > 1 && !borrower && !wantedPoolshipId) {
            stepNav.replace(1)
        } else if (step === 3 && borrower && !hasTerms) {
            stepNav.replace(2)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, borrower, wantedPoolshipId, hasTerms])

    const { title, subtitle } = STEP_TITLES[step]

    return (
        <WizardChrome
            title={title}
            subtitle={subtitle}
            step={step}
            totalSteps={TOTAL_STEPS}
            receipt={receipt}
            onBack={back}
            onJumpTo={stepNav.backTo}
            tallFooter={step === 3}
        >
            {step === 1 && (
                <StepBorrower
                    moneypoolId={moneypoolId}
                    selectedPoolshipId={borrower?.poolship_id}
                    onSelect={selectBorrower}
                />
            )}
            {step === 2 && (
                <>
                    <StepAmount
                        moneypoolId={moneypoolId}
                        amount={amount}
                        onChange={setAmount}
                        requestedAmount={borrower?.demand?.amount}
                    />
                    <p className=" mb-2 mt-5 text-xs font-bold text-heading">
                        بازپرداخت
                    </p>
                    <StepTerms
                        amount={Number(amount || 0)}
                        terms={terms}
                        onChange={setTerms}
                        schedule={{
                            ready: scheduleQuery.ready,
                            draft: scheduleQuery.draft,
                            isLoading: scheduleQuery.isLoading,
                            error: scheduleQuery.error,
                            schedule,
                            onPattern: choosePattern,
                            onEdit: editRows,
                            onReset: resetSchedule,
                            onFixRemainder: fixRemainder,
                        }}
                    />
                    <GuarantorSection
                        moneypoolId={moneypoolId}
                        borrowerPoolshipId={borrower?.poolship_id}
                        borrowerName={borrower?.member_name}
                        suggested={suggestion}
                        value={guarantors}
                        onChange={setGuarantors}
                        onOpen={() =>
                            posthog.capture(
                                'loan_grant_guarantor_list_opened',
                                {
                                    fund_id: moneypoolId,
                                    wizard_version: WIZARD_VERSION,
                                }
                            )
                        }
                    />
                </>
            )}
            {step === 3 && (
                <StepReview
                    moneypoolId={moneypoolId}
                    borrowerName={borrower?.member_name}
                    borrowerPoolshipId={borrower?.poolship_id}
                    borrowerMemberId={borrower?.member_id}
                    fundName={state?.name}
                    amount={Number(amount || 0)}
                    terms={terms}
                    schedule={schedule}
                    guarantors={guarantors}
                    note={note}
                    onNoteChange={setNote}
                    onCardChange={setCard}
                    selectedCard={card}
                    demand={borrower?.demand ?? undefined}
                    requestedCardId={
                        borrower?.demand?.selected_card?.id ?? undefined
                    }
                    isSubmitting={isSubmitting}
                    onGrant={() => submit(false)}
                    onRecordOnly={() => submit(true)}
                />
            )}

            {/* step 1 advances by picking somebody and step 3 has its own two
                outcomes, so only step 2 gets a «ادامه» bar */}
            {step === 2 && (
                <StickyAction
                    summary={
                        <TermsSummary
                            amount={Number(amount || 0)}
                            terms={terms}
                            schedule={schedule}
                        />
                    }
                >
                    <button
                        type="button"
                        onClick={onContinue}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        ادامه و بازبینی
                    </button>
                </StickyAction>
            )}
        </WizardChrome>
    )
}
